import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { usersAPI } from '../../lib/api';

// Roles assignable to firm staff (no "client" — that's a separate flow).
const STAFF_ROLES = [
  { value: 'admin', label: 'Firm Admin' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'bookkeeper', label: 'Bookkeeper' },
  { value: 'reviewer', label: 'Reviewer' },
  { value: 'read_only', label: 'Read-Only' },
];
const ROLE_LABELS = { owner: 'Super Admin', admin: 'Firm Admin', accountant: 'Accountant',
  staff: 'Accountant', bookkeeper: 'Bookkeeper', reviewer: 'Reviewer', read_only: 'Read-Only' };

export function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'accountant', password: '', send_invite: true });

  const loadUsers = async () => {
    try {
      const { data } = await usersAPI.list();
      setUsers(data.users || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await usersAPI.create(form);
      toast.success(`User ${form.email} created`);
      setAddOpen(false);
      setForm({ name: '', email: '', role: 'accountant', password: '', send_invite: true });
      await loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await usersAPI.update(userId, { role });
      toast.success('Role updated');
      await loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleToggleSuspend = async (u) => {
    const next = u.status === 'suspended' ? 'active' : 'suspended';
    try {
      await usersAPI.update(u.user_id, { status: next });
      toast.success(next === 'suspended' ? 'User suspended' : 'User reactivated');
      await loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Firm Users</CardTitle>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="add-user-button">
              <UserPlus className="w-4 h-4 mr-2" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Firm User</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STAFF_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Temporary Password *</Label>
                <label className="flex items-center gap-2 text-sm mb-1"><input type="checkbox" checked={form.send_invite} onChange={(e) => setForm({ ...form, send_invite: e.target.checked })} data-testid="user-send-invite" />Email an invite link so they set their own password</label>
                {!form.send_invite && <Input type="text" placeholder="Initial password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="user-initial-password" />}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={saving}>
                  {saving ? 'Creating…' : 'Create User'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-muted-foreground text-sm">Loading users…</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.user_id}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    {u.role === 'owner' ? (
                      <Badge className="bg-primary text-white">Super Admin</Badge>
                    ) : (
                      <Select value={u.role} onValueChange={(v) => handleRoleChange(u.user_id, v)}>
                        <SelectTrigger className="w-[150px] h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STAFF_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={u.status === 'suspended' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                      {u.status === 'suspended' ? 'Suspended' : 'Active'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {u.role !== 'owner' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className={u.status === 'suspended' ? 'text-primary' : 'text-red-600'}
                        onClick={() => handleToggleSuspend(u)}
                      >
                        {u.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
